import React from 'react';
import FocusTrap from 'focus-trap-react';
import useTranslation from '../../../../context/Translation/useTranslation';
import { useModal, ModalID } from '@repo/ui/src/modules/UI';
import CloseIcon from '@repo/ui/src/components/Icons/CloseIcon';
import { classNames } from '@repo/ui/src/utils/classNames';

export interface BaseModalProps {
  id: ModalID;
  children: React.ReactNode;
  className?: string;
  disableOutsideClose?: boolean;
  onClose?: () => void;
  fullScreen?: boolean;
  noButcherPaper?: boolean;
  disableClose?: boolean;
  'data-testid'?: string;
  srInstructions?: React.ReactNode;
}

interface LabelledByProps extends BaseModalProps {
  'aria-labelledby': string;
  'aria-label'?: never;
}

interface LabelledProps extends BaseModalProps {
  'aria-labelledby'?: never;
  'aria-label'?: string;
}

export type ModalProps = LabelledByProps | LabelledProps;

const Modal = ({
  id,
  className,
  onClose,
  children,
  fullScreen,
  noButcherPaper = false,
  disableClose = false,
  srInstructions,
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: ModalProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { active, zIndex, close } = useModal(id);
  const [hidden, setHidden] = React.useState(!active);
  const [visible, setVisible] = React.useState(active);

  React.useEffect(() => {
    if (active) {
      setHidden(false);
    } else {
      setVisible(false);
    }
  }, [active]);

  React.useEffect(() => {
    if (!hidden) {
      setVisible(true);
      ref.current?.focus();
    }
  }, [hidden]);

  const handleClose = () => {
    if (!disableClose) {
      onClose?.();
      close();
    }
  };
  if (hidden) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={classNames(
        'fixed inset-0 grid place-items-center motion-safe:transition-all duration-300',
        zIndex,
        visible ? 'backdrop-blur-md' : 'backdrop-blur-0 delay-100',
      )}
      onTransitionEnd={(e) => {
        if (!visible) {
          setHidden(true);
        } else {
          e.currentTarget.querySelector('div')?.focus();
        }
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <FocusTrap active={active} focusTrapOptions={{ allowOutsideClick: !disableClose }}>
        <div
          role="dialog"
          className={classNames(
            'relative scroll-auto overflow-auto motion-safe:transition-opacity grid grid-areas-overlap',
            visible ? 'delay-100 opacity-100' : 'opacity-0',
            !noButcherPaper && 'bg-neutral-700',
            fullScreen ? 'h-screen' : 'max-h-[95vh] border-brand-300 border-2 rounded-xl',
          )}
          data-testid={testId}
          aria-modal
          aria-labelledby={ariaLabelledBy}
          aria-label={ariaLabel}
          tabIndex={-1}
          aria-hidden={!active}
        >
          {srInstructions}
          {!noButcherPaper && (
            <div className="bg-butcher-paper inset-0 opacity-20 select-none grid-in-a" />
          )}
          {!disableClose && (
            <div className="absolute top-5 right-5 grid justify-end z-50">
              <button
                onClick={handleClose}
                className="p-1"
                aria-label={t('genericContent.closeIconButtonLabel', undefined, {
                  default: 'Close',
                })}
              >
                <CloseIcon
                  className={classNames(!noButcherPaper ? 'text-white' : 'text-neutral-500')}
                />
              </button>
            </div>
          )}
          <div className={classNames('grid-in-a', fullScreen && 'w-screen', className)}>
            {children}
          </div>
        </div>
      </FocusTrap>
    </div>
  );
};

export default Modal;
